import React from 'react';
import { useEnvironment } from '@/hooks/useEnvironment';
import { ArrowLeft, Mail, Calendar, CheckCircle, XCircle } from 'lucide-react';

const TermsofServices: React.FC = () => {
  const { isNativeWebView } = useEnvironment();

  // Desktop/Web Design - Full featured layout
  const WebDesign = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
            <CheckCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Terms of Services
          </h1>
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            <Calendar className="w-4 h-4 mr-2" />
            Last Updated: October 10, 2025
          </div>
        </div>

        {/* Terms Content */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 sm:p-8 lg:p-12 space-y-8">
              
              {/* Section 1 */}
              <section className="border-l-4 border-blue-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Acceptance of Terms</h2>
                    <p className="text-gray-700 leading-relaxed">
                      By accessing or using our website or services (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 2 */}
              <section className="border-l-4 border-green-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">User Eligibility</h2>
                    <p className="text-gray-700 leading-relaxed">
                      You must be at least 18 years old to use the Service. By using the Service, you represent that you meet this requirement.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 3 */}
              <section className="border-l-4 border-purple-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">User Accounts</h2>
                    <p className="text-gray-700 leading-relaxed">
                      You may need to create an account. You are responsible for keeping your account secure and for all activities under it.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 4 */}
              <section className="border-l-4 border-red-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Prohibited Activities</h2>
                    <div className="grid gap-3 mt-4">
                      <div className="flex items-start space-x-3">
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Violating laws or regulations.</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Harassing or harming others.</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Uploading viruses or harmful code.</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Using the Service for commercial purposes without permission.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 5 */}
              <section className="border-l-4 border-indigo-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-bold">5</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Intellectual Property</h2>
                    <p className="text-gray-700 leading-relaxed">
                      All content on the Service is owned by us or our licensors. You may not copy, modify, or distribute it without permission.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 6 */}
              <section className="border-l-4 border-yellow-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center text-sm font-bold">6</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Termination</h2>
                    <p className="text-gray-700 leading-relaxed">
                      We may suspend or terminate your access at any time for violating these Terms.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 7 */}
              <section className="border-l-4 border-pink-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold">7</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Disclaimer of Warranties</h2>
                    <p className="text-gray-700 leading-relaxed">
                      The Service is provided "as is" without warranties. We are not liable for any damages.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 8 */}
              <section className="border-l-4 border-teal-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">8</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Limitation of Liability</h2>
                    <p className="text-gray-700 leading-relaxed">
                      Our total liability shall not exceed $100.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 9 */}
              <section className="border-l-4 border-orange-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">9</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Governing Law</h2>
                    <p className="text-gray-700 leading-relaxed">
                      These Terms are governed by the laws of [Your Country/State].
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 10 */}
              <section className="border-l-4 border-cyan-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center text-sm font-bold">10</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Changes to Terms</h2>
                    <p className="text-gray-700 leading-relaxed">
                      We may update these Terms. Continued use means you accept changes.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 11 */}
              <section className="border-l-4 border-emerald-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold">11</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Contact Us</h2>
                    <div className="flex items-center space-x-3 mt-3">
                      <Mail className="w-5 h-5 text-emerald-500" />
                      <span className="text-gray-700 font-medium">support@duriapp.com</span>
                    </div>
                  </div>
                </div>
              </section>

            </div>

            {/* Footer */}
            {/* <div className="bg-gray-50 px-6 sm:px-8 lg:px-12 py-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                <p className="text-sm text-gray-600 text-center sm:text-left">
                  © 2025 Your Company. All rights reserved.
                </p>
                <div className="flex items-center space-x-4">
                  <button className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                    Privacy Policy
                  </button>
                  <button className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                    Cookie Policy
                  </button>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="fixed bottom-8 right-8">
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all duration-300 flex items-center justify-center group"
          >
            <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  // Mobile WebView Design - iPhone-style, clean, edge-to-edge
  const WebViewDesign = () => (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
     

      {/* Content */}
      <div className="space-y-3 px-4 py-4 pb-20">
        
        {/* Header Card */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-sm p-6 border border-blue-100">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 rounded-2xl mb-4 shadow-lg">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-bold text-gray-900 text-xl mb-3">Terms of Services</h1>
            <div className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-full text-sm font-medium shadow-sm">
              <Calendar className="w-4 h-4 mr-2" />
              Last Updated: October 10, 2025
            </div>
          </div>
        </div>

        {/* Terms Sections */}
        <div className="space-y-3">
          
          {/* Section 1 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">1</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">Acceptance of Terms</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  By accessing or using our website or services (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-green-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">2</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">User Eligibility</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  You must be at least 18 years old to use the Service. By using the Service, you represent that you meet this requirement.
                </p>
              </div>
            </div>
          </div>

          {/* Section 3 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-purple-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">3</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">User Accounts</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  You may need to create an account. You are responsible for keeping your account secure and for all activities under it.
                </p>
              </div>
            </div>
          </div>

          {/* Section 4 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-red-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">4</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">Prohibited Activities</h3>
                <div className="space-y-3 mt-3">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <XCircle className="w-3 h-3 text-red-600" />
                    </div>
                    <span className="text-gray-700 text-sm">Violating laws or regulations.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <XCircle className="w-3 h-3 text-red-600" />
                    </div>
                    <span className="text-gray-700 text-sm">Harassing or harming others.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <XCircle className="w-3 h-3 text-red-600" />
                    </div>
                    <span className="text-gray-700 text-sm">Uploading viruses or harmful code.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <XCircle className="w-3 h-3 text-red-600" />
                    </div>
                    <span className="text-gray-700 text-sm">Using the Service for commercial purposes without permission.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-indigo-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">5</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">Intellectual Property</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  All content on the Service is owned by us or our licensors. You may not copy, modify, or distribute it without permission.
                </p>
              </div>
            </div>
          </div>

          {/* Section 6 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-yellow-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">6</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">Termination</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  We may suspend or terminate your access at any time for violating these Terms.
                </p>
              </div>
            </div>
          </div>

          {/* Section 7 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-pink-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">7</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">Disclaimer of Warranties</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  The Service is provided "as is" without warranties. We are not liable for any damages.
                </p>
              </div>
            </div>
          </div>

          {/* Section 8 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-teal-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">8</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">Limitation of Liability</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Our total liability shall not exceed $100.
                </p>
              </div>
            </div>
          </div>

          {/* Section 9 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-orange-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">9</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">Governing Law</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  These Terms are governed by the laws of [Your Country/State].
                </p>
              </div>
            </div>
          </div>

          {/* Section 10 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-cyan-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">10</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">Changes to Terms</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  We may update these Terms. Continued use means you accept changes.
                </p>
              </div>
            </div>
          </div>

          {/* Section 11 */}
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-emerald-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">11</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-3">Contact Us</h3>
                <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 p-4 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-emerald-600" />
                    <span className="text-emerald-800 text-sm font-medium">support@Duriapp.com</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl shadow-sm p-6 mt-4 border border-gray-200">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-200 rounded-xl mb-3">
              <CheckCircle className="w-6 h-6 text-gray-600" />
            </div>
            <p className="text-sm text-gray-700 mb-4 font-medium">
              © 2025 Duri App Technologies Pvt. Ltd. All rights reserved.
            </p>
            
          </div>
        </div>

      </div>
    </div>
  );

  // Conditional rendering based on environment
  return isNativeWebView ? <WebViewDesign /> : <WebDesign />;
};

export default TermsofServices;